import { createFunction, useTranslation } from '@msdk/common'
import {
  Entity,
  EntityContext,
  FormMode,
  FormNode,
  LogicalPath,
  ServerOperationResult,
  TenantPath,
  UserPath,
} from '@msdk/definitions'
import {
  ButtonPrimary,
  ButtonSecondary,
  CardActions,
  CardBody,
  FormCard as BaseFormCard,
  IconSelector,
  Spinner,
  Status,
  styled,
  Typography,
  uiTokens,
} from '@msdk/ui'
import React, { useState } from 'react'

import { useDialogModal } from '../../hooks'
import { useEmbeddedForm } from '../../hooks/form'
import { useTabs } from '../../utils'
import { saveValidated, ValidatedForm } from '../../utils/save-validation'
import { DynamicForm } from '../DynamicForm'
import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'

/**
 * When enabled, the container entity id is used as fallback
 * when neither `id` nor `parentId` are provided to the embedded form.
 */
const USE_CONTAINER_ENTITY_ID_AS_FALLBACK = false

const FormCard = styled(BaseFormCard)`
  margin-bottom: 16px;
`

const NoIdCardBody = styled(CardBody)`
  && {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    min-height: 128px;
  }
`

const NoIdMessage = () => {
  const t = useTranslation()

  return (
    <NoIdCardBody>
      <Status severity="info">{t('LBL_EMBEDDED_FORM_NO_ID', 'No id or parentId selected')}</Status>
    </NoIdCardBody>
  )
}

const MessageCardBody = styled(CardBody)`
  && {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0 32px 0;
  }
`

/**
 * Right padding fix
 */
const FormCardBody = styled(CardBody)`
  margin-right: -${uiTokens.spacing.gap.large}px;
`

interface CommnProps {
  entityName: string
  formName: string
  containerEntity: Entity
}

interface EmbeddedFormProps extends CommnProps {
  id?: string
  parentId?: string
}

interface EmbeddedFormContentProps extends CommnProps {
  id: string
}

interface EmbeddedFormCardSlotContextValue {
  renderForm: (mode: FormMode) => React.ReactNode
  editFooter: React.ReactNode
}

const EmbeddedFormCardSlotContext = React.createContext<EmbeddedFormCardSlotContextValue | undefined>(undefined)

const EmbeddedFormViewSlot = () => {
  const context = React.useContext(EmbeddedFormCardSlotContext)
  return context?.renderForm('Readonly') ?? null
}

const EmbeddedFormEditSlot = () => {
  const context = React.useContext(EmbeddedFormCardSlotContext)
  if (!context) return null

  return (
    <>
      {context.renderForm('Edit')}
      {context.editFooter}
    </>
  )
}

function resolveId(containerEntity: Entity, id?: string, parentId?: string): string | undefined {
  const expression = id ?? parentId
  if (!expression) {
    return USE_CONTAINER_ENTITY_ID_AS_FALLBACK ? containerEntity.id : undefined
  }

  const func = createFunction(expression)
  return func({
    entity: new EntityContext(containerEntity, {} as FormNode),
    row: undefined,
    logic: new LogicalPath(),
    user: new UserPath(),
    tenant: new TenantPath(),
  })
}

function EmbeddedFormContent(props: EmbeddedFormContentProps) {
  const t = useTranslation()
  const validationRef = React.useRef<(() => Promise<ValidatedForm | null>) | null>(null)
  const dialog = useDialogModal()
  const [mode, setMode] = useState<FormMode>('Readonly')
  const id = props.id

  const {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    onSave,
    acl,
    invoker,
    onReloadEntity,
    hasError,
    onClose: onCloseForm,
  } = useEmbeddedForm(props.entityName, props.formName, id, undefined, props.containerEntity)
  const { selectedTab, updateSelectedTab, startingTab } = useTabs({ form: form as FormNode })

  const title = t(formModel.idml, formModel.description)

  if (hasError) {
    const error = () => (
      <MessageCardBody>
        <Status severity="error">{t('LBL_ERROR', 'Error occured')}</Status>
      </MessageCardBody>
    )
    return <FormCard header={{ title }} showEditButton={false} view={error} edit={error} />
  }

  if (!formModel || !form) {
    const loading = () => (
      <MessageCardBody>
        <Spinner />
      </MessageCardBody>
    )
    return <FormCard header={{ title }} showEditButton={false} view={loading} edit={loading} />
  }

  if (formModel.componentDefinition?.fullName) {
    const unsupportedForm = () => (
      <FormCardBody>
        <Typography variant="body2">
          {t(
            'LBL_EMBEDDED_FORM_CUSTOM_FORM_NOT_SUPPORTED',
            'Custom form components are not supported in embedded forms.',
          )}
        </Typography>
      </FormCardBody>
    )
    return <FormCard header={{ title }} showEditButton={false} view={unsupportedForm} edit={unsupportedForm} />
  }

  const renderForm = (formMode: FormMode) => (
    <FormCardBody className="EmbeddedDynamicForm">
      <DynamicForm
        root={form as FormNode}
        entityLoaded={entityLoaded}
        formModel={formModel}
        entity={entity}
        additionalEntities={additionalEntities}
        entityModel={entityModel}
        acl={acl}
        invoker={invoker as CustomLogicInvoker}
        mode={formMode}
        onDataChanged={() => onReloadEntity()}
        validationRef={validationRef}
        selectedTab={selectedTab}
        onChangeTab={updateSelectedTab}
        startingTab={startingTab}
        isEmbedded={true}
      />
    </FormCardBody>
  )

  const onSaveClick = async () => {
    const result = await saveValidated(validationRef.current, onSave)
    if (result.result === ServerOperationResult.Success) {
      setMode('Readonly')
    }
    return result
  }

  const onCancel = async () => {
    const { canLeaveForm } = await onCloseForm(dialog)
    if (!canLeaveForm) return

    await onReloadEntity()
    setMode('Readonly')
  }

  const editFooter = (
    <CardActions>
      <ButtonSecondary startIcon={<IconSelector iconName="Close" />} onClick={() => void onCancel()}>
        {t('LBL_EMBEDDED_FORM_CANCEL', 'Exit editing')}
      </ButtonSecondary>
      <ButtonPrimary startIcon={<IconSelector iconName="Check" />} onClick={() => void onSaveClick()}>
        {t('LBL_EMBEDDED_FORM_SAVE', 'Save changes')}
      </ButtonPrimary>
    </CardActions>
  )

  return (
    <EmbeddedFormCardSlotContext.Provider value={{ renderForm, editFooter }}>
      <FormCard
        header={{ title }}
        mode={mode === 'Edit' ? 'edit' : 'view'}
        onModeChange={(nextMode) => {
          setMode(nextMode === 'edit' ? 'Edit' : 'Readonly')
        }}
        showEditButton={mode !== 'Edit' && acl.canUpdate}
        view={EmbeddedFormViewSlot}
        edit={EmbeddedFormEditSlot}
      />
    </EmbeddedFormCardSlotContext.Provider>
  )
}

export function EmbeddedForm(props: EmbeddedFormProps) {
  const id = resolveId(props.containerEntity, props.id, props.parentId)

  if (!id) {
    return <FormCard mode="view" showEditButton={false} view={NoIdMessage} edit={NoIdMessage} />
  }

  return <EmbeddedFormContent {...props} id={id} />
}
